import {
  Button, Textarea,
  RecordInterface, RecordOptionsInterface,
} from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';

/** Props */
interface JobInterviewQuestionProps {
  record: RecordInterface,
  recordOptions?: RecordOptionsInterface,
  saveQuestions?: () => void,
  deleteQuestion: () => void,
  inputName?: string,
  showDeleteButton?: boolean,
  currentIndex?: number,
  enabled?: boolean,
  onToggleEnabled?: (checked: boolean) => void,
  readOnly?: boolean,
}

/**
 * @description Job Interview Question component. Has own delete button.
 * - `record` - record interface from useBjForm
 * - `recordOptions` - record options interface from useBjForm
 * - `saveQuestions` - save questions function
 * - `deleteQuestion` - delete question function
 * - `inputName` - name of the input; default is 'interviewQuestions'
 * -` showDeleteButton` - whether to show the delete button; default is true
 * - `currentIndex` - current index of the question
 * - `enabled` - whether the question is enabled
 * - `onToggleEnabled` - toggle enabled handler
 */
export const JobInterviewQuestion = (props: JobInterviewQuestionProps) => {
  // Destructure props
  const {
    record,
    recordOptions,
    saveQuestions,
    deleteQuestion,
    inputName = 'interviewQuestions',
    showDeleteButton = true,
    currentIndex,
    enabled = true,
    onToggleEnabled,
    readOnly = false,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Render
  // **************************
  return (
    <div className="flex w-full items-center gap-2">
      {/* Question */}
      <Textarea
        onBlur={saveQuestions}
        rows={2}
        // Safari does not support field-sizing; it will default to two rows
        className="[field-sizing:content]"
        {...record(inputName, recordOptions)}
        readOnly={readOnly}
        aria-readonly={readOnly}
      />

      <Switch
        checked={enabled}
        onChange={(value) => onToggleEnabled?.(value)}
        className={`
                relative inline-flex h-6 w-12 items-center rounded-full transition
                ${enabled ? 'bg-green-500' : 'bg-gray-300'}
              `}
        aria-label={enabled ? t('global.action.disable') : t('global.action.enable')}
        title={enabled ? t('global.action.disable') : t('global.action.enable')}
        data-test-id={`jobAdd-toggle-interview-question-enabled_${currentIndex}`}
      >
        <span
          className={`
                  inline-block size-4 rounded-full bg-white transition
                  ${enabled ? 'translate-x-6' : 'translate-x-1'}
                `}
        />
      </Switch>

      {/* Delete question button */}
      {showDeleteButton && (
        <Button
          color="light"
          styling="outline"
          size="xs"
          className="shrink-0 !px-1"
          title={t('global.action.remove')}
          onClick={deleteQuestion}
          disabled={readOnly}
          aria-disabled={readOnly}
          data-test-id={`jobAdd-delete-interview-question-button_${currentIndex}`}
        >
          <TrashIcon className="size-4 text-error/80" />
        </Button>
      )}
    </div>
  );
};
